import * as React from "react";
import { EyeOpenIcon, EyeSlashIcon } from "assets/website";
import type { UseFormRegister } from "react-hook-form";

interface InputProps {
  type?: string;
  accept?: string;
  dataTestID?: string;
  placeholder: string;
  className?: string;
  parentClassName?: string;
  name: string;
  id?: string;
  required?: boolean;
  minLength?: number;
  validatorMessage: string | undefined;
  label?: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  iconFunction?: () => void;
  register: UseFormRegister<any>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  min?: any;
  max?: number | string;
  readOnly?: boolean;
  value?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  showRequired?: boolean;
  disabled?: boolean;
}

const Input: React.FC<InputProps> = ({
  type,
  dataTestID,
  placeholder,
  className,
  parentClassName,
  name,
  accept,
  id,
  required,
  minLength,
  validatorMessage,
  label,
  Icon,
  iconClassName,
  iconFunction,
  register,
  onChange,
  min,
  readOnly,
  value,
  onKeyDown,
  showRequired,
  disabled,
  onFocus,
  max,
}) => {
  const [show, setShow] = React.useState(false);

  return (
    <div
      className={`relative flex flex-col text-left bg-transparent mb-6 w-full ${parentClassName}`}
    >
      {label && (
        <label className={`text-sm bg-transparent p-0 font-medium mb-2 ${validatorMessage ? "text-red-500" : "text-[#344054]"}`}>
          {label}
          {showRequired ? <span className="text-red-500 font-semibold">*</span> : ""}
        </label>
      )}
      {type === "password" ? (
        <>
          {!show ? (
            <EyeOpenIcon
              role="button"
              className={`absolute right-2.5 top-10 w-5 h-auto cursor-pointer ${iconClassName}`}
              onClick={() => setShow(!show)}
            />
          ) : (
            <EyeSlashIcon
              className={`absolute right-2.5 top-10 w-5 h-auto cursor-pointer ${iconClassName}`}
              onClick={() => setShow(!show)}
            />
          )}
        </>
      ) : (
        <>
          {Icon ? (
            <Icon
              role={iconFunction ? "button" : "none"}
              onClick={iconFunction && iconFunction}
              className={`absolute right-2.5 top-10 w-5 h-auto cursor-pointer ${iconClassName}`}
              id="icon"
            />
          ) : (
            ""
          )}
        </>
      )}
      <input
        data-testid={dataTestID}
        className={`h-11 pl-4 bg-transparent text-sm pr-2.5 !text-black caret-black w-full rounded-md border disabled:cursor-not-allowed focus:outline-none placeholder:!text-[#98A2B3] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:shadow-none [&:-webkit-autofill]:[-webkit-text-fill-color:#000000] ${
          validatorMessage ? "border-red-500" : "border-[#dae0e6]"
        } ${
          value !== "" ? "bg-transparent" : ""
        } ${className}`}
        type={show ? "text" : type}
        placeholder={placeholder}
        accept={accept}
        id={id}
        {...register(name, {
          required: required,
          minLength: minLength,
          onChange: onChange,
          min: min,
          max: max,
        })}
        min={min}
        readOnly={readOnly}
        value={value}
        onKeyDown={onKeyDown}
        disabled={disabled}
        onFocus={onFocus}
        style={{
          WebkitBoxShadow: '0 0 0 1000px transparent inset',
          backgroundColor: 'transparent',
        }}
      />

      {validatorMessage && (
        <small className="text-xs text-red-500 block mt-2">{validatorMessage}</small>
      )}
    </div>
  );
};

const Textarea: React.FC<InputProps> = ({
  dataTestID,
  placeholder,
  className,
  parentClassName,
  name,
  id,
  required,
  minLength,
  validatorMessage,
  label,
  Icon,
  iconClassName,
  iconFunction,
  register,
  onChange,
  min,
  readOnly,
  value,
}) => {
  return (
    <div
      className={`relative flex flex-col text-left bg-transparent mb-6 w-full ${parentClassName}`}
    >
      {label && <label className={`text-sm bg-transparent p-0 font-medium mb-2 ${validatorMessage ? "text-red-500" : "text-[#101323]"}`}>{label}</label>}
      {Icon ? (
        <Icon
          role={iconFunction ? "button" : "none"}
          onClick={iconFunction && iconFunction}
          className={`absolute right-2.5 top-10 w-5 h-auto cursor-pointer ${iconClassName}`}
          id="icon"
        />
      ) : (
        ""
      )}
      <textarea
        data-testid={dataTestID}
        className={`pl-4 pt-3 rounded-md shadow-sm bg-transparent text-sm resize-none h-50 outline-none placeholder:text-[#98A2B3] ${
          validatorMessage ? "border-red-500" : "border-[#dae0e6]"
        } ${
          value !== "" ? "bg-transparent" : ""
        } ${className}`}
        placeholder={placeholder}
        id={id}
        {...register(name, {
          required: required,
          minLength: minLength,
          onChange: onChange,
          min: min,
        })}
        readOnly={readOnly}
        value={value}
      />

      {validatorMessage && (
        <small className="text-xs text-red-500 block mt-2">{validatorMessage}</small>
      )}
    </div>
  );
};

export { Input, Textarea };
